import { Button } from "@/components/common/Button";
import { useUserStore } from "@/modules/auth/store/user";
import { formatNumber } from "@/modules/auth/utils/format";
import { getTimeLeftUntil } from "@/modules/auth/utils/time";
import { constants } from "@/modules/calculations/consts";
import { useMatchingBonusValueStore } from "@/modules/matching-bonus/store/value";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
}

const RewardsMatchingBonusBASE = () => {
  const { t } = useTranslation();
  const { matchingBonusBASE: matchingBonus, tonPrice, price } = useUserStore();

  const { setNet } = useMatchingBonusValueStore();

  const navigate = useNavigate();

  const goToWithdraw = async () => {
    setNet("base");

    await navigate("/matching-bonus");
  };

  const [withdrawInfo, setWithdrawInfo] = useState<{
    text: string;
    disabled: boolean;
  }>({
    text: "",
    disabled: true,
  });

  useEffect(() => {
    const oneTONInBTN = (1 * tonPrice) / price;
    const nextStart = getTimeLeftUntil(matchingBonus.nextStartTimeWithdraw);
    const nextEnd = getTimeLeftUntil(matchingBonus.nextEndTimeWithdraw);
    // const nextStart = getTimeLeftUntil("2025-12-26T05:00:00.000Z");
    // const nextEnd = getTimeLeftUntil("2025-12-27T04:59:59.000Z");

    const getTimeParts = (time: TimeLeft): string[] => {
      const parts: string[] = [];
      if (time.days > 0)
        parts.push(t("main-page.withdraw.time.day", { count: time.days }));
      if (time.hours > 0)
        parts.push(t("main-page.withdraw.time.hour", { count: time.hours }));
      if (time.minutes > 0)
        parts.push(
          t("main-page.withdraw.time.minute", { count: time.minutes })
        );
      return parts;
    };

    const resolveParts = (time: TimeLeft) => {
      const parts = getTimeParts(time);
      const first = parts[0] || "";
      const second = parts[1] || "";
      return { first, second };
    };

    if (nextStart.days > 0 || nextStart.hours > 0 || nextStart.minutes > 0) {
      const { first, second } = resolveParts(nextStart);
      setWithdrawInfo({
        text: t("main-page.withdraw.will_available", { first, second }),
        disabled: true,
      });
      return;
    }

    if (matchingBonus.tokenAmount < oneTONInBTN) {
      setWithdrawInfo({
        text: t("main-page.withdraw.min", {
          amount: formatNumber(oneTONInBTN, {
            precision: constants.TOKEN_DECIMALS,
          }),
          token: "BTN",
        }),
        disabled: true,
      });
      return;
    }

    if (nextEnd.days > 0 || nextEnd.hours > 0 || nextEnd.minutes > 0) {
      const { first, second } = resolveParts(nextEnd);
      setWithdrawInfo({
        text: t("main-page.withdraw.open", { first, second }),
        disabled: false,
      });
      return;
    }

    setWithdrawInfo({ text: "", disabled: true });
  }, [matchingBonus, tonPrice, price, t]);

  return (
    <div className="flex flex-col bg-bg rounded-[0.75rem]">
      <div className="flex items-center justify-between px-4">
        <div className="flex-1 flex flex-col items-start gap-0.5 py-3">
          <div className="flex items-end gap-1">
            <p className="text-[1.5rem] leading-[1.75rem] font-semibold text-text">
              {formatNumber(matchingBonus.tokenAmount, {
                precision: 3,
              })}
            </p>
            <p className="text-[1.0625rem] leading-[1.375rem] text-hint pb-px">
              BTN
            </p>
          </div>

          <p className="text-[0.9375rem] leading-[1.25rem] text-subtitle-text">
            {t("main-page.matching_bonus_balance")} BASE
          </p>
        </div>

        <p className="text-[1.0625rem] leading-[1.375rem] text-text">
          $
          {formatNumber(matchingBonus.usdAmount, {
            precision: 2,
          })}
        </p>
      </div>

      <div className="flex flex-col gap-1 w-full p-4 pt-2">
        <Button
          onClick={goToWithdraw}
          text={t("main-page.withdraw_matching_bonus")}
          icon="archive"
          mode="bezeled"
          className="w-full"
          disabled={withdrawInfo.disabled}
          //   disabled={true}
        />
        <p className="pt-2 text-[0.8125rem] leading-[1rem] text-section-header-text">
          {withdrawInfo.text}
        </p>
      </div>
    </div>
  );
};

export default RewardsMatchingBonusBASE;
